import { logError } from "../datadog";
import type { CdcUserAttributes } from "../models/cdcUser";
import { CookieService } from "./cookies";
import {
  localStorageService,
  STORAGE_KEYS,
  sessionStorageService,
} from "./storage";

const CDC_JWT_TIMEOUT_MS = 10_000;
const CDC_JWT_EXPIRATION_SECONDS = 3600;
const CDC_TOKEN_LIFETIME_MS = 3000 * 1000;
const ACCESS_DENIED_MESSAGE =
  "You are not allowed to access this application, access will be enabled in 10 min or else contact system admin";
const SESSION_REFRESH_FAILED_MESSAGE =
  "Failed to refresh session. Please log in again.";

/**
 * Gigya `403005 Unauthorized User` is the documented "no valid session" verdict
 * (SAP KBA 2702693) — the one CX-3151 is about. Only this code means the session
 * is genuinely dead; server errors (`500xxx`), timeouts (`504001`), and network
 * failures are transient and must stay retryable, or a blip would lock the user
 * out until they manually log in again.
 */
const CDC_SESSION_DEAD_ERROR_CODES = new Set([403005]);

const isSessionDeadErrorCode = (errorCode?: number): boolean =>
  errorCode !== undefined && CDC_SESSION_DEAD_ERROR_CODES.has(errorCode);

/**
 * Which of the four independent refresh triggers made the call. A failure in
 * the request interceptor and one in the background watcher are different bugs,
 * but they used to be indistinguishable in RUM — every report read
 * `source: "CdcTokenService"`.
 */
export type CdcRefreshTrigger =
  | "request-interceptor"
  | "retry-401"
  | "gigya-ready"
  | "expiry-watcher";

/**
 * Reads only the `exp` claim of the stored JWT. The token must never leave the
 * browser through RUM, so nothing else is decoded and the payload is dropped
 * immediately. A missing or malformed token yields `undefined` rather than
 * throwing — this runs on an error path and must not mask the original failure.
 */
const readJwtExpiryMs = (): number | undefined => {
  const segment = CookieService.getToken()?.split(".")[1];
  if (!segment) return undefined;
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  try {
    const { exp } = JSON.parse(
      atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")),
    );
    return typeof exp === "number" ? exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

/**
 * CX-3151: the app records "this user is logged in" in four places with four
 * different lifetimes — the Gigya session (dies with the browser), the
 * `authToken` cookie (7 days), `tokenExpiredTime` (written 50 min ahead), and
 * the JWT's own `exp` (1 hour). A report is only explainable with all of them in
 * view, so every one carries this snapshot. `cookieVsJwtSkewMs` is the
 * divergence itself: positive means the cookie still claims a session the JWT
 * has already ended, which is the suspected root cause rather than a symptom.
 */
const describeSessionState = (): Record<string, unknown> => {
  const now = Date.now();
  const cookieExpiry = CookieService.getTokenExpiredTime();
  const cookieExpiresInMs = cookieExpiry
    ? new Date(cookieExpiry).getTime() - now
    : undefined;
  const jwtExpiry = readJwtExpiryMs();
  const jwtExpiresInMs = jwtExpiry === undefined ? undefined : jwtExpiry - now;

  return {
    hasToken: CookieService.getToken() !== undefined,
    // False means nothing in the app ever believed this visitor was logged in —
    // the SDK resurrected a dead cookie on its own.
    hasCdcUser: localStorageService.getItem(STORAGE_KEYS.CDC_USER) !== null,
    cookieExpiresInMs,
    jwtExpiresInMs,
    cookieVsJwtSkewMs:
      cookieExpiresInMs === undefined || jwtExpiresInMs === undefined
        ? undefined
        : cookieExpiresInMs - jwtExpiresInMs,
  };
};

interface GigyaJwtResponse {
  errorCode?: number;
  errorMessage?: string;
  errorDetails?: string;
  callId?: string;
  statusCode?: number;
  statusReason?: string;
  apiVersion?: number;
  time?: string;
}

/**
 * Gigya responses can carry `regToken` (a credential, on pending
 * registration/TFA verdicts) and `id_token` — neither may reach RUM, which runs
 * at `defaultPrivacyLevel: "allow"`. Pick the diagnostic fields explicitly
 * instead of spreading the raw response, so a future Gigya field can't leak by
 * default. `callId` is the handle SAP support needs to trace a call server-side.
 */
const describeGigyaResponse = (
  response: GigyaJwtResponse | undefined,
): Record<string, unknown> => ({
  errorCode: response?.errorCode,
  errorMessage: response?.errorMessage,
  errorDetails: response?.errorDetails,
  callId: response?.callId,
  statusCode: response?.statusCode,
  statusReason: response?.statusReason,
  apiVersion: response?.apiVersion,
  time: response?.time,
});

let inflightRefresh: Promise<string | undefined> | null = null;

/**
 * CX-3151: Gigya sessions are created with `sessionExpiration: 0` (they die when
 * the browser closes), but Color Finder's own view of "this user is logged in"
 * outlives them — `cdcUser` in localStorage never expires and the `authToken`
 * cookie lasts 7 days. Once the Gigya session is gone, every `accounts.getJWT`
 * answers 403005 "Unauthorized user" and the SDK runs `logoutFromProvidersIfNeeded`.
 *
 * Nothing recorded that verdict, so all four refresh triggers (request
 * interceptor, 401 retry, the 30s expiry poll, and `onGigyaServiceReady`) kept
 * re-asking a question Gigya had already answered — 110 errors across 26 sessions
 * in 14 days, bursting several times per second. This latch makes the answer
 * stick: tear the local session down once, then stop asking until a fresh login.
 */
let cdcSessionInvalid = false;

/**
 * CX-3151: a `getJWT` callback answers the session state from *when it was
 * issued*. A background refresh can be in flight (up to the 10s timeout) while
 * the user logs in again in the same tab; if that stale call then resolves
 * 403005 it would tear down the fresh, valid login. Every login bumps this
 * counter, each `getJWT` captures it at issue time, and `invalidateSession`
 * ignores any verdict from an older generation.
 */
let sessionGeneration = 0;

// biome-ignore lint/complexity/noStaticOnlyClass: Matches AuthService pattern
export class CdcTokenService {
  static isGigyaAvailable(): boolean {
    return typeof window.gigya?.accounts?.getJWT === "function";
  }

  static isTokenExpiringSoon(bufferMs = 300_000): boolean {
    return CookieService.isTokenExpiringSoon(bufferMs);
  }

  static async refreshToken(
    trigger: CdcRefreshTrigger,
  ): Promise<string | undefined> {
    if (cdcSessionInvalid) return undefined;
    if (inflightRefresh) return inflightRefresh;

    inflightRefresh = CdcTokenService.doRefresh(trigger).finally(() => {
      inflightRefresh = null;
    });

    return inflightRefresh;
  }

  /**
   * Gigya has told us the session is gone. Clear the local state that made the
   * app believe otherwise and announce it once, so the header can prompt for a
   * new login instead of every caller retrying independently.
   */
  private static invalidateSession(
    errorCode?: number,
    generation = sessionGeneration,
  ): void {
    // A newer login started (and bumped the generation) after this getJWT was
    // issued — its verdict is stale and must not tear down the fresh session.
    if (generation !== sessionGeneration) return;
    // Only a documented "no valid session" verdict latches. Transient failures
    // (server error, timeout, network) must remain retryable.
    if (!isSessionDeadErrorCode(errorCode)) return;
    if (cdcSessionInvalid) return;
    cdcSessionInvalid = true;

    // Deliberately not reported: the guard above means this point is only ever
    // reached for 403005, i.e. an expected dead session, not a fault.
    localStorageService.removeItem(STORAGE_KEYS.CDC_USER);
    // Without this the next login in the same tab writes a valid token but
    // `loadAccountInfoAndReload` skips its reload, leaving the UI logged out.
    sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
    CookieService.removeToken();
    // CX-3744: detecting a dead session (page-load cookie-restore, background
    // refresh) clears the logged-out state but never opens the dialog on its
    // own. A request that 401s mid-use shows it from the 401 interceptor.
    window.dispatchEvent(
      new CustomEvent("auth:session-expired", {
        detail: { showDialog: false },
      }),
    );
  }

  /**
   * Re-arm refreshing. Called whenever a fresh login attempt starts, so a user
   * who was locked out by an expired Gigya session can sign in again without
   * reloading the page.
   */
  static markSessionValid(): void {
    cdcSessionInvalid = false;
    // Supersede any getJWT already in flight so its late verdict can't undo the
    // login that is about to happen.
    sessionGeneration += 1;
  }

  /**
   * Fetches a fresh CDC JWT, then loads account info and reloads the page once.
   * Used after a successful Gigya login or registration to propagate the new
   * auth state across the SPA. Reload is gated by STORAGE_KEYS.RELOADED so it
   * runs at most once per session.
   */
  static getCdcJwtAndReload(setGlobalError: (msg: string) => void): void {
    // CX-3151 follow-up: nothing writes "onCdcLogin" anymore (the flag moved
    // to the in-memory `pendingCdcLogin` in CovestroHeader.tsx), but a
    // previously-deployed build could have left "true" in a returning user's
    // localStorage — it never expires on its own. Keep this one-time cleanup
    // so stale keys don't linger indefinitely.
    localStorage.removeItem("onCdcLogin");
    // A login attempt supersedes any earlier "session is dead" verdict.
    CdcTokenService.markSessionValid();
    const generation = sessionGeneration;
    window.gigya?.accounts.getJWT({
      fields: "firstName, lastName, city, country, address, zip, email, locale",
      expiration: CDC_JWT_EXPIRATION_SECONDS,
      callback: (response) => {
        if (response?.id_token) {
          CookieService.setToken(response.id_token);
          CookieService.setTokenExpiredTime(
            new Date(Date.now() + CDC_TOKEN_LIFETIME_MS),
          );
          CdcTokenService.loadAccountInfoAndReload(setGlobalError);
        } else {
          const errorCode = (response as { errorCode?: number } | undefined)
            ?.errorCode;
          // 403005 "Unauthorized user" is not an error to surface — it just
          // means there is no live Gigya session (e.g. the SDK fired `onLogin`
          // from a stale cookie while the user was only browsing). Tear the
          // session down silently; only genuine failures (server error,
          // timeout, network) warrant the toast and a reported error.
          if (!isSessionDeadErrorCode(errorCode)) {
            // The Error message stays constant so Datadog groups every
            // occurrence into one issue; the varying detail goes in the context.
            logError(new Error("CDC getJWT returned no id_token"), {
              source: "CdcTokenService.getCdcJwtAndReload",
              trigger: "login",
              ...describeGigyaResponse(response),
              ...describeSessionState(),
            });
            setGlobalError(SESSION_REFRESH_FAILED_MESSAGE);
          }
          CdcTokenService.invalidateSession(errorCode, generation);
        }
      },
    });
  }

  private static loadAccountInfoAndReload(
    setGlobalError: (msg: string) => void,
  ): void {
    window.gigya?.accounts.getAccountInfo({
      extraProfileFields: "work,address",
      callback: (response) => {
        if (response.errorCode !== 0) {
          setGlobalError(`Error: ${response.errorMessage}`);
          console.error(`Error response: ${JSON.stringify(response)}`);
          console.error(`3. ${ACCESS_DENIED_MESSAGE}`);
          CookieService.removeToken();
          return;
        }

        (window.gigya?.accounts as any)?.groups?.getAllMemberGroups({
          uid: response.UID,
          callback: (grpResponse: { errorCode: number; results: any }) => {
            const inAppGroup =
              grpResponse.errorCode === 0 &&
              grpResponse.results?.some(
                (group: { groupId: string }) =>
                  group.groupId === "cov_imagio_cf",
              );

            if (!inAppGroup) {
              const stage = grpResponse.errorCode === 0 ? "1" : "2";
              console.error(`${stage}. ${ACCESS_DENIED_MESSAGE}`);
              setGlobalError(ACCESS_DENIED_MESSAGE);
              CookieService.removeToken();
              localStorage.removeItem("onCdcLogin");
              return;
            }

            if (!response.profile) return;

            const cdcUserResponse: CdcUserAttributes = {
              firstName: response.profile.firstName,
              lastName: response.profile.lastName,
              email: response.profile.email,
              country: response.profile.country,
              city: response.profile.city,
              address: response.profile.address,
              zip: response.profile.zip,
              locale: response.profile.locale,
              company: (response.data?.work as any)?.cov_company,
              extData: response.data,
            };
            localStorage.setItem("cdcUser", JSON.stringify(cdcUserResponse));

            // CX-3151: the reload is a one-shot per tab, so a second login in
            // the same tab (after a session expiry) used to write a perfectly
            // valid session that the header never picked up — the user stayed
            // "logged out" no matter how often they signed in. Announce the
            // login so React can re-read the cache with or without a reload.
            window.dispatchEvent(new CustomEvent("auth:cdc-login"));

            if (!sessionStorageService.getItem(STORAGE_KEYS.RELOADED)) {
              sessionStorageService.setItem(STORAGE_KEYS.RELOADED, "true");
              window.location.reload();
            }
          },
        });
      },
    });
  }

  private static doRefresh(
    trigger: CdcRefreshTrigger,
  ): Promise<string | undefined> {
    if (!CdcTokenService.isGigyaAvailable()) {
      return Promise.resolve(undefined);
    }

    const generation = sessionGeneration;
    return new Promise<string | undefined>((resolve) => {
      const timeout = setTimeout(() => {
        // No response exists here, so the session snapshot is the only evidence
        // separating a hung SDK from a token that was already dead on arrival.
        logError(new Error("CDC token refresh timed out"), {
          source: "CdcTokenService",
          trigger,
          timeoutMs: CDC_JWT_TIMEOUT_MS,
          ...describeSessionState(),
        });
        resolve(undefined);
      }, CDC_JWT_TIMEOUT_MS);

      window.gigya?.accounts.getJWT({
        fields:
          "firstName, lastName, city, country, address, zip, email, locale",
        expiration: CDC_JWT_EXPIRATION_SECONDS,
        callback: (response) => {
          clearTimeout(timeout);
          if (response?.id_token) {
            CookieService.setToken(response.id_token);
            CookieService.setTokenExpiredTime(
              new Date(Date.now() + CDC_TOKEN_LIFETIME_MS),
            );
            resolve(response.id_token);
          } else {
            const errorCode = (response as { errorCode?: number } | undefined)
              ?.errorCode;
            if (!isSessionDeadErrorCode(errorCode)) {
              logError(new Error("CDC token refresh returned no id_token"), {
                source: "CdcTokenService",
                trigger,
                ...describeGigyaResponse(response),
                ...describeSessionState(),
              });
            }
            // Gigya answered, and the answer was "no session". Retrying on the
            // next request/poll/focus only repeats it — latch the verdict.
            CdcTokenService.invalidateSession(errorCode, generation);
            resolve(undefined);
          }
        },
      });
    });
  }
}
